import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

/**
 * Modal queue / orchestration layer (ACC-399 bug bash #1626 — "too many pop-ups").
 *
 * Problem this solves: at app/dashboard load, ~8 independent modals each own a
 * `show` boolean and race to open (minor-consent gate, profile reminder, three
 * confetti variants, POK cert, partner-pay deeplink, and soon the product tour).
 * They stack on top of each other. #1618 patched the worst case (suppress while
 * the consent gate is up); this store is the real fix.
 *
 * Two concepts, deliberately kept separate:
 *   - OFFSET  — the short gap between one modal closing and the next opening,
 *               so it reads as "one thing at a time" (sub-second, session-only).
 *   - COOLDOWN — a per-modal "don't re-nag for N ms" policy, persisted across
 *               sessions (this is where the team's 12h profile-reminder
 *               deferment lives). Optional per descriptor.
 *
 * Blockers: the minor-consent gate is a hard, legal block — it is NOT a queue
 * item. Instead it registers as a *blocker*: while any blocker is active the
 * queue pauses and nothing dequeues (mirrors the existing tour `blocked`
 * callback in App.vue). When the last blocker clears, the queue pumps.
 *
 * Usage (consumer side):
 *   const q = useModalQueueStore();
 *   // when your data-condition becomes true:
 *   q.requestShow({ id: 'profile-reminder', priority: 40, cooldownMs: 12 * 60 * 60 * 1000, persistKey: learnerId });
 *   // bind your dialog:  v-model="q.isCurrent('profile-reminder')" ... @close="q.dismiss('profile-reminder')"
 *   // the consent gate registers a blocker instead:
 *   watch(() => minorConsent.blocksPortal, (v) => q.setBlocked('consent-gate', v), { immediate: true });
 */

const DEFAULT_OFFSET_MS = 600;

// localStorage is wrapped: it can throw (private mode, quota) and is absent in
// some test/SSR contexts. `window` itself may be an unbound identifier in pure
// Node/SSR (a bare reference throws ReferenceError, which optional chaining does
// NOT guard), so probe it with typeof first. Cooldown is a best-effort nicety,
// never load-bearing.
function getLocalStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function readLastShown(storageKey) {
  try {
    const raw = getLocalStorage()?.getItem(storageKey);
    const ts = raw == null ? NaN : Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function writeLastShown(storageKey, ts) {
  try {
    getLocalStorage()?.setItem(storageKey, String(ts));
  } catch {
    // ignore — cooldown just won't persist this time
  }
}

// A consumer's canShow() closes over its component instance and can throw (e.g.
// reading a computed on a consumer that has since unmounted). Treat any throw as
// "not eligible right now" so a single bad descriptor can never crash pump() and
// wedge the entire queue.
function canShowSafely(item) {
  try {
    return item.canShow();
  } catch {
    return false;
  }
}

export const useModalQueueStore = defineStore('modalQueue', () => {
  // Descriptors waiting to be shown, keyed by id. A Map keeps insertion order
  // stable for equal-priority ties (first requested wins).
  const queue = ref(new Map());

  // The id of the modal currently on screen, or null.
  const current = ref(null);

  // Named active blockers (e.g. 'consent-gate'). While non-empty, the queue is
  // paused. Named so multiple, independent blockers compose safely.
  const blockers = ref(new Set());

  // Pending offset timer between dismiss and the next show.
  let offsetTimer = null;

  const offsetMs = ref(DEFAULT_OFFSET_MS);

  const isBlocked = computed(() => blockers.value.size > 0);
  const isIdle = computed(() => current.value === null);

  function cooldownKey(item) {
    // Namespaced by persistKey (typically the learner id) so cooldowns don't
    // leak across accounts on a shared device.
    return `modalQueue:lastShown:${item.persistKey ?? 'global'}:${item.id}`;
  }

  function isCoolingDown(item, now) {
    if (!item.cooldownMs) return false;
    const last = readLastShown(cooldownKey(item));
    if (last == null) return false;
    return now - last < item.cooldownMs;
  }

  // Pick the highest-priority queued item that is still eligible right now.
  // Eligibility is re-checked at pump time (not enqueue time) because a modal's
  // condition often only resolves after async data lands during load.
  function pickNext() {
    const now = Date.now();
    let best = null;
    for (const item of queue.value.values()) {
      if (isCoolingDown(item, now)) continue;
      if (typeof item.canShow === 'function' && !canShowSafely(item)) continue;
      if (best === null || item.priority > best.priority) best = item;
    }
    return best;
  }

  // Show the next eligible modal if the queue is free to do so. Safe to call
  // repeatedly — it no-ops when blocked, when one is already showing, or when
  // nothing is eligible.
  function pump() {
    if (isBlocked.value || current.value !== null) return;
    const next = pickNext();
    if (!next) return;

    queue.value.delete(next.id);
    current.value = next.id;

    if (next.cooldownMs) writeLastShown(cooldownKey(next), Date.now());
  }

  // A consumer asks to show its modal. Returns true if it is showing or pending,
  // false if refused (no id, or within its cooldown window on a fresh request).
  //
  // A re-request of a still-queued item refreshes its descriptor (priority/canShow)
  // — so a remounted consumer's live closure replaces a stale one — and re-pumps so
  // its eligibility is re-evaluated. Without this, an item enqueued while canShow
  // was false could sit forever even after conditions change.
  function requestShow(descriptor) {
    const item = { priority: 0, ...descriptor };
    if (!item.id) return false;
    if (current.value === item.id) return true;

    const alreadyQueued = queue.value.has(item.id);
    // Cooldown only refuses a *fresh* request; a queued item stays queued.
    if (!alreadyQueued && isCoolingDown(item, Date.now())) return false;

    queue.value.set(item.id, item);
    pump();
    return true;
  }

  function isCurrent(id) {
    return current.value === id;
  }

  // Dismiss the on-screen modal. Clears `current`, then after `offsetMs` shows
  // the next eligible item. Only the modal that is actually current can dismiss
  // the slot (guards against a stale close handler clobbering a newer modal).
  function dismiss(id) {
    if (current.value !== id) return;
    current.value = null;

    if (offsetTimer) clearTimeout(offsetTimer);
    offsetTimer = setTimeout(() => {
      offsetTimer = null;
      pump();
    }, offsetMs.value);
  }

  // Register/clear a named blocker. Clearing the last one resumes the queue.
  function setBlocked(name, blocked) {
    if (blocked) {
      blockers.value.add(name);
    } else {
      blockers.value.delete(name);
      pump();
    }
  }

  // Drop a queued (not-yet-shown) request, e.g. its condition became false.
  function cancel(id) {
    queue.value.delete(id);
    if (current.value === id) dismiss(id);
  }

  function setOffsetMs(ms) {
    offsetMs.value = ms;
  }

  return {
    // state
    current,
    isBlocked,
    isIdle,
    offsetMs,
    // queries
    isCurrent,
    // commands
    requestShow,
    dismiss,
    cancel,
    setBlocked,
    setOffsetMs,
    pump
  };
});
